import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { AccountLayout } from '@/components/account/AccountLayout'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'

const MIN_PASSWORD_LENGTH = 8

// Plain logged-in "change your password" page. The password-recovery flow
// (forgot password → email link) is handled entirely by the public
// /reset-password page, so this page no longer special-cases a recovery mode.
export function AccountSecurityPage() {
  const { user, loading: authLoading } = useAuth()

  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setFeedback(null)
    if (next !== confirm) {
      setFeedback({ kind: 'err', text: 'Passwords do not match.' })
      return
    }
    if (next.length < MIN_PASSWORD_LENGTH) {
      setFeedback({ kind: 'err', text: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.` })
      return
    }
    setSaving(true)
    const { error } = await supabase.auth.updateUser({ password: next })
    setSaving(false)
    if (error) setFeedback({ kind: 'err', text: error.message })
    else {
      setNext('')
      setConfirm('')
      setFeedback({ kind: 'ok', text: 'Password updated.' })
    }
  }

  if (!authLoading && !user) return <Navigate to="/sign-in" replace />

  return (
    <AccountLayout>
      <header className="mb-8">
        <h1 className="text-[26px] font-semibold tracking-tight text-[var(--color-text-strong)]">
          Security
        </h1>
        <p className="mt-1 text-[14px] text-[var(--color-muted)]">
          Change your password. Every other signed-in device is signed out
          automatically on a successful change.
        </p>
      </header>
      <SecurityRedirectBanner />

      <section className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            id="new-password"
            label="New password"
            type="password"
            value={next}
            onChange={setNext}
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
          />
          <Input
            id="confirm-password"
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={setConfirm}
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
          />
          <div className="flex items-center justify-between gap-3">
            {feedback ? (
              <span
                className={[
                  'text-[12.5px]',
                  feedback.kind === 'ok'
                    ? 'text-[var(--color-accent)]'
                    : 'text-[var(--color-signal)]',
                ].join(' ')}
              >
                {feedback.text}
              </span>
            ) : (
              <span />
            )}
            <SubmitButton submitting={saving}>Update password</SubmitButton>
          </div>
        </form>
      </section>
    </AccountLayout>
  )
}

function SecurityRedirectBanner() {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const message =
    params.get('password_updated') === '1' ? 'Your password has been updated.' : null

  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => {
      params.delete('password_updated')
      const next = params.toString()
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true })
    }, 5000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])

  if (!message) return null
  return (
    <div
      role="status"
      className={[
        'mb-6 flex items-center gap-3',
        'rounded-[var(--radius-DEFAULT)] border border-[var(--color-accent)]/40',
        'bg-[var(--color-accent-soft)] px-4 py-3',
        'text-[13.5px] text-[var(--color-text-strong)]',
      ].join(' ')}
    >
      <CheckCircle2 size={16} className="text-[var(--color-accent)] shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  )
}

interface InputProps {
  id: string
  label: string
  type?: 'text' | 'email' | 'password' | 'tel'
  value: string
  onChange: (value: string) => void
  minLength?: number
  autoComplete?: string
}
function Input({ id, label, type = 'text', value, onChange, minLength, autoComplete }: InputProps) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
        {label}
      </span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        minLength={minLength}
        className={[
          'w-full px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
          'bg-[var(--color-surface)]',
          'border border-[var(--color-border)]',
          'text-[14px] text-[var(--color-text)]',
          'focus:outline-none focus:border-[var(--color-accent)]',
          'focus:ring-2 focus:ring-[var(--color-accent)]/30',
          'transition-colors',
        ].join(' ')}
      />
    </label>
  )
}

interface SubmitButtonProps { children: React.ReactNode; submitting: boolean }
function SubmitButton({ children, submitting }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={submitting}
      className={[
        'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-[var(--radius-DEFAULT)]',
        'bg-[var(--color-accent)] text-[var(--color-surface)] text-[13px] font-medium',
        'hover:bg-[var(--color-accent-hover)] transition-colors',
        'disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer',
      ].join(' ')}
    >
      {submitting ? 'Working…' : children}
    </button>
  )
}
